// FormReadService.js

/**
 * FormReadService.js
 *
 * Service for reading form template documents from Firestore.
 * When `typeKey` is set, merges live system-type forms
 * (`_nodetypes/{typeKey}/forms`) ahead of base-scoped `_forms`.
 */

import { collection, doc, getDoc, onSnapshot, query, where } from 'firebase/firestore';
import { collections, db } from 'firebaseConfig';

import { normalizeFormRow } from 'services/forms/FormModel';
import {
  mergeSystemAndBaseForms,
  subscribeToTypeForms,
} from 'services/forms/TypeFormService';
import { sortLayoutsByConfiguredOrder } from 'utilities/LayoutOrder';

const FORMS_COLLECTION = collections.forms;

function normalizeString(value) {
  return typeof value === 'string' ? value.trim() : '';
}

function sortForms(rows = []) {
  return sortLayoutsByConfiguredOrder(rows);
}

export function subscribeToForms({ baseKey, typeKey } = {}, callback) {
  if (typeof callback !== 'function') {
    return () => {};
  }

  const cleanedBaseKey = normalizeString(baseKey);
  if (!cleanedBaseKey) {
    callback([]);
    return () => {};
  }

  const cleanedTypeKey = normalizeString(typeKey);
  const constraints = [where('baseKey', '==', cleanedBaseKey)];
  if (cleanedTypeKey) {
    constraints.push(where('typeKey', '==', cleanedTypeKey));
  }

  const q = query(collection(db, FORMS_COLLECTION), ...constraints);

  if (!cleanedTypeKey) {
    return onSnapshot(
      q,
      (snapshot) => {
        callback(sortForms(snapshot.docs.map((docSnap) => normalizeFormRow(docSnap))));
      },
      (error) => {
        console.error('[FormReadService] subscribeToForms error:', error);
        callback([]);
      },
    );
  }

  let baseRows = [];
  let systemRows = [];
  let cancelled = false;

  const emit = () => {
    if (cancelled) return;
    callback(mergeSystemAndBaseForms(systemRows, baseRows));
  };

  const unsubscribeBase = onSnapshot(
    q,
    (snapshot) => {
      baseRows = snapshot.docs.map((docSnap) => normalizeFormRow(docSnap));
      emit();
    },
    (error) => {
      console.error('[FormReadService] subscribeToForms error:', error);
      baseRows = [];
      emit();
    },
  );

  const unsubscribeSystem = subscribeToTypeForms(cleanedTypeKey, (rows) => {
    systemRows = Array.isArray(rows) ? rows : [];
    emit();
  });

  return () => {
    cancelled = true;
    unsubscribeBase();
    unsubscribeSystem();
  };
}

export async function getForm(formKey) {
  const cleanedFormKey = normalizeString(formKey);
  if (!cleanedFormKey) return null;

  const docSnap = await getDoc(doc(db, FORMS_COLLECTION, cleanedFormKey));
  return docSnap.exists() ? normalizeFormRow(docSnap) : null;
}

export function subscribeToForm(formKey, callback) {
  if (typeof callback !== 'function') {
    return () => {};
  }

  const cleanedFormKey = normalizeString(formKey);
  if (!cleanedFormKey) {
    callback(null);
    return () => {};
  }

  return onSnapshot(
    doc(db, FORMS_COLLECTION, cleanedFormKey),
    (docSnap) => {
      callback(docSnap.exists() ? normalizeFormRow(docSnap) : null);
    },
    (error) => {
      console.error('[FormReadService] subscribeToForm error:', error);
      callback(null);
    },
  );
}
